// engine-exception: the invoices resource exposes lines & payments sub-collection routes (GET/POST/PATCH/DELETE) that collide by action name in /v1/meta, so the generic detail toolbar renders each non-GET route as a button and cannot hide a duplicate-named action by method — a bespoke financial document until the engine can allowlist primary actions
import { useActionServed } from '../../engine/meta/useMeta';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  LifecycleRail,
  Money,
  PageHeader,
  Progress,
  Select,
  Skeleton,
  Stat,
  StatusBadge,
  Tabs,
  useApi,
  useApiQuery,
  useInvalidateResource,
  useMeta,
  useResource,
  useToast,
  ActionButtons,
  resolveActions,
  useActionRunner,
  type LifecycleStep,
} from '../../engine';
import { MoneyEditor } from '../../engine/fields/registry';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { ActivityFeed } from '../../engine/views/ActivityFeed';
import { NotesPanel } from '../../engine/views/NotesPanel';
import { AttachmentsPanel } from '../../engine/views/AttachmentsPanel';
import { ApiError } from '../../engine/api/client';
import { formatDateTime, formatMoney, sumMinor } from '../../lib/format';
import { field } from './lib';

// Invoice lifecycle workflow on engine parts: PageHeader + LifecycleRail, a receipt of
// Stats/Progress, the issue/freeze/void/credit-note actions from /v1/meta (confirm dialogs +
// `when` guards run by the engine action runner), inline-editable draft lines, and payments
// with record + reverse. Every mutation is a resource action on /v1/invoices.

type MoneyDto = { minor: string; currency: string } | null;
interface Invoice {
  id: string;
  number: string | null;
  kind: string;
  status: string;
  currency: string;
  client_name: string | null;
  issue_date: string | null;
  due_date: string | null;
  total: MoneyDto;
  subtotal: MoneyDto;
  tax: MoneyDto;
  outstanding: MoneyDto;
  paid: MoneyDto;
}
interface Line { id: string; line_no: number; description: string; quantity: string; unit_price_minor: string; net_minor: string; tax_minor: string }
interface Payment { id: string; amount: { minor: string; currency: string }; paid_at: string; method: string | null; reference: string | null; reversed: boolean }

const HEADER_ACTIONS = new Set(['issue', 'freeze', 'void', 'credit-note']);
const PAYMENT_METHODS = ['bank_transfer', 'card', 'check', 'cash', 'other'];

function steps(inv: Invoice): LifecycleStep[] {
  if (inv.status === 'voided') {
    const s: LifecycleStep[] = [{ label: 'Draft', state: 'done' }];
    if (inv.number) s.push({ label: 'Issued', state: 'done' });
    s.push({ label: 'Voided', state: 'terminal' });
    return s;
  }
  const order = ['draft', 'issued', 'frozen'];
  const idx = order.indexOf(inv.status);
  return order.map((v, i) => ({ label: v[0]!.toUpperCase() + v.slice(1), state: i < idx ? 'done' : i === idx ? 'current' : 'upcoming' }));
}

export function InvoiceDetailScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const meta = useMeta('invoices');
  const rowQ = useResource<Invoice>('invoices', id);
  const inv = rowQ.data;
  const linesQ = useApiQuery<{ data: Line[] }>(['invoice-lines', id], `/v1/invoices/${id}/lines`, { enabled: Boolean(id) });
  // Payments are NovaPSA's ledger; a connected system (Halo) does not serve them, so they are not asked for.
  const hasPayments = useActionServed('invoices', ':id/payments');
  const paymentsQ = useApiQuery<{ data: Payment[] }>(['invoice-payments', id], `/v1/invoices/${id}/payments`, { enabled: Boolean(id) && hasPayments });
  const [tab, setTab] = useState('lines');

  const refresh = () => {
    void rowQ.refetch();
    void linesQ.refetch();
    void paymentsQ.refetch();
  };
  const runner = useActionRunner('invoices', {
    onDone: (action, result) => {
      refresh();
      if (action.name === 'credit-note' && result && typeof result === 'object' && 'id' in result) navigate(`/invoices/${(result as { id: string }).id}`);
    },
  });

  if (rowQ.error) return <div className="hf-page"><ErrorBanner error={rowQ.error} action={<Button size="sm" variant="ghost" onClick={() => navigate('/invoices')}>Back</Button>} /></div>;
  if (!inv) return <div className="hf-page"><Skeleton height={28} width={240} /><div style={{ marginTop: 16 }}><Skeleton lines={6} /></div></div>;

  const cur = inv.currency;
  const lines = linesQ.data?.data ?? [];
  const payments = paymentsQ.data?.data ?? [];
  const isDraft = inv.status === 'draft';
  const posted = inv.status === 'issued' || inv.status === 'frozen';
  const subtotal = isDraft ? sumMinor(lines, (l) => l.net_minor) : Number(inv.subtotal?.minor ?? inv.total?.minor ?? 0);
  const tax = isDraft ? sumMinor(lines, (l) => l.tax_minor) : Number(inv.tax?.minor ?? 0);
  const total = isDraft ? subtotal + tax : Number(inv.total?.minor ?? 0);
  const outstanding = inv.outstanding ? Number(inv.outstanding.minor) : null;
  const paid = outstanding != null ? total - outstanding : null;
  const overdue = posted && (outstanding ?? 0) > 0 && !!inv.due_date && new Date(inv.due_date).setHours(23, 59, 59, 999) < Date.now();

  const actions = meta.data ? resolveActions(meta.data, undefined, 'item', inv as unknown as Record<string, unknown>).filter((a) => HEADER_ACTIONS.has(a.name)) : [];

  const tabs = [
    { key: 'lines', label: 'Lines', icon: 'list' as const },
    ...(hasPayments ? [{ key: 'payments', label: 'Payments', icon: 'banknote' as const, count: payments.length || undefined }] : []),
    { key: 'activity', label: 'Activity', icon: 'activity' as const },
    { key: 'notes', label: 'Notes', icon: 'sticky-note' as const },
    { key: 'files', label: 'Files', icon: 'paperclip' as const },
  ];

  return (
    <div className="hf-page">
      <PageHeader
        compact
        breadcrumb={[{ label: 'Invoices', to: '/invoices' }, { label: inv.number ?? 'Draft' }]}
        title={inv.number ?? 'Draft invoice'}
        badge={<span className="hf-row" style={{ gap: 'var(--s-1)' }}><StatusBadge status={inv.status} />{overdue && <StatusBadge status="overdue" />}</span>}
        subtitle={inv.client_name ? `${inv.client_name} · ${inv.kind.replace(/_/g, ' ')}` : inv.kind.replace(/_/g, ' ')}
        actions={<ActionButtons actions={actions} onRun={runner.run} ctx={{ id, row: inv as unknown as Record<string, unknown>, refresh }} max={3} />}
      />
      <LifecycleRail steps={steps(inv)} />

      <div className="hf-stats" aria-label="Invoice totals">
        <Stat label="Subtotal" value={<Money minor={subtotal} currency={cur} />} />
        <Stat label="Tax" value={<Money minor={tax} currency={cur} />} />
        <Stat label={isDraft ? 'Total so far' : 'Total'} value={<Money minor={total} currency={cur} />} accent />
        {posted && outstanding != null && <Stat label="Outstanding" value={<Money minor={outstanding} currency={cur} color />} sub={paid != null ? `${formatMoney(paid, cur)} collected` : undefined} />}
      </div>
      {posted && outstanding != null && total > 0 && (
        <Progress value={(paid ?? 0) / total} tone={overdue ? 'warn' : undefined} title={`${formatMoney(paid, cur)} of ${formatMoney(total, cur)} collected`} />
      )}
      {inv.status === 'frozen' && <Banner tone="info" icon="lock">Frozen — financially immutable. Corrections are a credit note or a void, never an edit.</Banner>}

      <Tabs tabs={tabs} value={tab} onChange={setTab} aria-label="Invoice sections" />
      <div className="hf-tabpanel" role="tabpanel">
        {tab === 'lines' && <LinesTab invoiceId={id} currency={cur} lines={lines} loading={linesQ.isLoading} error={linesQ.error} editable={isDraft} onChange={refresh} />}
        {tab === 'payments' && <PaymentsTab invoiceId={id} currency={cur} payments={payments} loading={paymentsQ.isLoading} error={paymentsQ.error} canPay={posted && inv.kind === 'invoice' && (outstanding ?? 0) > 0} outstanding={outstanding} voided={inv.status === 'voided'} onChange={refresh} />}
        {tab === 'activity' && <ActivityFeed entityType="invoice" entityId={id} />}
        {tab === 'notes' && <NotesPanel entityType="invoice" entityId={id} />}
        {tab === 'files' && <AttachmentsPanel entityType="invoice" entityId={id} />}
      </div>
      {runner.dialogs}
    </div>
  );
}

const DESC_FIELD = field({ name: 'description', type: 'text' });
const QTY_FIELD = field({ name: 'quantity', type: 'number', align: 'right' });
const UNIT_FIELD = field({ name: 'unit_price_minor', type: 'money', currencyField: 'currency', align: 'right' });
const TAX_FIELD = field({ name: 'tax_minor', type: 'money', currencyField: 'currency', align: 'right' });

function LinesTab({ invoiceId, currency, lines, loading, error, editable, onChange }: { invoiceId: string; currency: string; lines: Line[]; loading: boolean; error: unknown; editable: boolean; onChange: () => void }) {
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateResource();
  const rows = useMemo(() => lines.map((l) => ({ ...l, currency })), [lines, currency]);

  const patch = async (lineId: string, body: Record<string, unknown>) => {
    try {
      await api.request(`/v1/invoices/${invoiceId}/lines/${lineId}`, { method: 'PATCH', body });
      onChange();
      invalidate('invoices', invoiceId);
    } catch (e) {
      toast({ kind: 'error', title: 'Could not update line', detail: e instanceof ApiError ? e.detail : String(e) });
    }
  };
  const del = async (lineId: string) => {
    try {
      await api.request(`/v1/invoices/${invoiceId}/lines/${lineId}`, { method: 'DELETE' });
      onChange();
      invalidate('invoices', invoiceId);
      toast({ title: 'Line removed', detail: 'Any linked time returned to the billing run.' });
    } catch (e) {
      toast({ kind: 'error', title: 'Could not remove line', detail: e instanceof ApiError ? e.detail : String(e) });
    }
  };

  const columns: GridColumn<Line & { currency: string }>[] = [
    { id: 'line_no', header: '#', width: 48, align: 'right', cell: (l) => <span className="hf-muted hf-num">{l.line_no}</span> },
    { id: 'description', header: 'Description', field: { ...DESC_FIELD, inUpdate: editable } },
    { id: 'quantity', header: 'Qty', align: 'right', field: { ...QTY_FIELD, inUpdate: editable } },
    { id: 'unit_price_minor', header: 'Unit price', align: 'right', field: { ...UNIT_FIELD, inUpdate: editable } },
    { id: 'net_minor', header: 'Net', align: 'right', cell: (l) => <Money minor={l.net_minor} currency={currency} /> },
    { id: 'tax_minor', header: 'Tax', align: 'right', field: { ...TAX_FIELD, inUpdate: editable } },
    ...(editable ? [{ id: 'del', header: '', width: 90, align: 'right' as const, cell: (l: Line) => <Button size="sm" variant="ghost" icon="trash" aria-label={`Remove line ${l.line_no}`} onClick={() => void del(l.id)} /> }] : []),
  ];

  return (
    <div className="hf-stack">
      <DataGrid<Line & { currency: string }>
        aria-label="Invoice lines"
        columns={columns}
        rows={rows}
        keyOf={(l) => l.id}
        loading={loading}
        error={error}
        hideToolbar
        empty={<EmptyState title="No line items" hint={editable ? 'Add a line to build this invoice.' : undefined} icon="list" />}
        onCellEdit={editable ? (l, f, value) => patch(l.id, { [f.name]: value }) : undefined}
      />
      {editable && <AddLineForm invoiceId={invoiceId} currency={currency} onAdded={onChange} />}
    </div>
  );
}

function AddLineForm({ invoiceId, currency, onAdded }: { invoiceId: string; currency: string; onAdded: () => void }) {
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateResource();
  const [desc, setDesc] = useState('');
  const [qty, setQty] = useState('1');
  const [unit, setUnit] = useState<number | null>(null);
  const [tax, setTax] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const q = Number(qty) || 1;
  const preview = unit != null && unit > 0 ? q * unit + (tax ?? 0) : null;

  const submit = async () => {
    if (unit == null || unit <= 0) return;
    setBusy(true);
    try {
      await api.request(`/v1/invoices/${invoiceId}/lines`, { method: 'POST', body: { description: desc.trim() || 'Services', quantity: q, unit_price_minor: unit, net_minor: Math.round(q * unit), ...(tax != null ? { tax_minor: tax } : {}) } });
      setDesc('');
      setQty('1');
      setUnit(null);
      setTax(null);
      onAdded();
      invalidate('invoices', invoiceId);
      toast({ title: 'Line added' });
    } catch (e) {
      toast({ kind: 'error', title: 'Could not add line', detail: e instanceof ApiError ? e.detail : String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="hf-row" style={{ alignItems: 'flex-end', gap: 'var(--s-2)', flexWrap: 'wrap' }} onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <Field label="Description" id="line-desc" className="hf-grow"><Input id="line-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Monthly managed services" /></Field>
      <Field label="Qty" id="line-qty"><Input id="line-qty" value={qty} onChange={(e) => setQty(e.target.value)} numeric inputMode="decimal" style={{ width: 80 }} /></Field>
      <Field label="Unit price" id="line-unit"><div style={{ width: 150 }}><MoneyEditor id="line-unit" field={UNIT_FIELD} value={unit} onChange={(v) => setUnit(typeof v === 'number' ? v : null)} currency={currency} row={{ currency }} /></div></Field>
      <Field label="Tax" id="line-tax" optional><div style={{ width: 130 }}><MoneyEditor id="line-tax" field={TAX_FIELD} value={tax} onChange={(v) => setTax(typeof v === 'number' ? v : null)} currency={currency} row={{ currency }} /></div></Field>
      <Button type="submit" variant="primary" icon="plus" busy={busy} disabled={unit == null || unit <= 0}>Add {preview != null ? formatMoney(preview, currency) : 'line'}</Button>
    </form>
  );
}

const PAY_FIELD = field({ name: 'amount', type: 'money', currencyField: 'currency' });

function PaymentsTab({ invoiceId, currency, payments, loading, error, canPay, outstanding, voided, onChange }: { invoiceId: string; currency: string; payments: Payment[]; loading: boolean; error: unknown; canPay: boolean; outstanding: number | null; voided: boolean; onChange: () => void }) {
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateResource();
  const [amount, setAmount] = useState<number | null>(null);
  const [method, setMethod] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const over = amount != null && outstanding != null && amount > outstanding;

  const record = async () => {
    if (amount == null || amount <= 0 || over) return;
    setBusy(true);
    try {
      await api.request(`/v1/invoices/${invoiceId}/payments`, { method: 'POST', body: { amount_minor: amount, method, ...(reference.trim() ? { reference: reference.trim() } : {}) } });
      setAmount(null);
      setReference('');
      onChange();
      invalidate('invoices', invoiceId);
      toast({ title: `Payment recorded — ${formatMoney(amount, currency)}` });
    } catch (e) {
      toast({ kind: 'error', title: 'Could not record payment', detail: e instanceof ApiError ? e.detail : String(e) });
    } finally {
      setBusy(false);
    }
  };
  const reverse = async (paymentId: string) => {
    try {
      await api.request(`/v1/invoices/${invoiceId}/payments/${paymentId}/reverse`, { method: 'POST', body: {} });
      onChange();
      invalidate('invoices', invoiceId);
      toast({ title: 'Payment reversed', detail: 'Outstanding was restored by a reversal entry.' });
    } catch (e) {
      toast({ kind: 'error', title: 'Could not reverse payment', detail: e instanceof ApiError ? e.detail : String(e) });
    }
  };

  const columns: GridColumn<Payment>[] = [
    { id: 'paid_at', header: 'Received', width: 170, cell: (p) => <span className="hf-num">{formatDateTime(p.paid_at)}</span> },
    { id: 'method', header: 'Method', cell: (p) => (p.method ? <span className="hf-mono">{p.method.replace(/_/g, ' ')}</span> : '—') },
    { id: 'reference', header: 'Reference', cell: (p) => p.reference ?? <span className="hf-muted">—</span> },
    { id: 'amount', header: 'Amount', align: 'right', cell: (p) => <Money minor={p.amount.minor} currency={p.amount.currency} /> },
    { id: 'act', header: '', width: 100, align: 'right', cell: (p) => (p.reversed ? <Badge tone="muted" dot={false}>reversed</Badge> : <Button size="sm" variant="ghost" icon="undo" disabled={voided} onClick={() => void reverse(p.id)}>Reverse</Button>) },
  ];

  return (
    <div className="hf-stack">
      <DataGrid<Payment>
        aria-label="Payments"
        columns={columns}
        rows={payments}
        keyOf={(p) => p.id}
        loading={loading}
        error={error}
        hideToolbar
        empty={<EmptyState title="No payments yet" hint={canPay ? 'Record one when the money lands.' : undefined} icon="banknote" />}
      />
      {canPay && (
        <form className="hf-row" style={{ alignItems: 'flex-end', gap: 'var(--s-2)', flexWrap: 'wrap' }} onSubmit={(e) => { e.preventDefault(); void record(); }}>
          <Field label="Amount received" id="pay-amount" error={over ? 'More than the outstanding balance — the ledger will refuse an overpayment.' : undefined}>
            <div style={{ width: 160 }}><MoneyEditor id="pay-amount" field={PAY_FIELD} value={amount} onChange={(v) => setAmount(typeof v === 'number' ? v : null)} currency={currency} row={{ currency }} /></div>
          </Field>
          <Field label="Method" id="pay-method"><Select id="pay-method" value={method} onChange={(e) => setMethod(e.target.value)} options={PAYMENT_METHODS.map((m) => ({ value: m, label: m.replace(/_/g, ' ') }))} /></Field>
          <Field label="Reference" id="pay-ref" optional><Input id="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Deposit ref, check #…" /></Field>
          <Button type="submit" variant="primary" icon="banknote" busy={busy} disabled={amount == null || amount <= 0 || over}>Record payment</Button>
          {outstanding != null && outstanding > 0 && <Button type="button" variant="ghost" onClick={() => setAmount(outstanding)}>Full ({formatMoney(outstanding, currency)})</Button>}
        </form>
      )}
    </div>
  );
}
